import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import DOMPurify from "dompurify";
import {
	Accordion,
	AccordionButton,
	AccordionIcon,
	AccordionItem,
	AccordionPanel,
	Box,
	Text,
} from "@chakra-ui/react";
import { FiMessageSquare } from "react-icons/fi";

import { RichTextEditor, RichTextEditorHandle } from "newui/components/ui/RichTextEditor/RichTextEditor";
import { getQuoteComment, saveQuoteComment } from "../../api";

export interface QuoteCommentProps {
	initialComment?: string;
	inviteId: number;
	isReadOnly: boolean;
	/**
	 * Called with the persisted comment right after a genuine save succeeds
	 * (never for a swallowed spurious-empty save — see PRO-659 handling in
	 * `handleSave`). Lets the parent keep its own copy of the quote model in
	 * sync so a later unmount/remount (tab switch) reseeds `initialComment`
	 * from current data instead of the stale eager-loaded value.
	 */
	onSaved?: (comment: string) => void;
	quoteId: number;
}

export interface QuoteCommentHandle {
	/**
	 * Cancel any pending debounced save and persist the latest comment
	 * immediately, awaiting the request. Parents (BidderQuoteTab's
	 * `validateAndSave`) MUST await this before letting a tab switch
	 * proceed — otherwise a comment edited within the debounce window is
	 * silently dropped when this component unmounts, even though the
	 * outer tab-switch gate shows a "Saving…" state for something else
	 * entirely. Resolves `false` (never throws) if the save failed, so
	 * the caller can avoid treating a failed flush as a clean save.
	 */
	flushPending: () => Promise<boolean>;
}

const DEBOUNCE_MS = 1000;

const QuoteComment = forwardRef<QuoteCommentHandle, QuoteCommentProps>(({
	initialComment,
	inviteId,
	isReadOnly,
	onSaved,
	quoteId,
}, ref) => {
	const [comment, setComment] = useState(initialComment ?? "");
	const [isSaving, setIsSaving] = useState(false);
	const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const isInitialMount = useRef(true);
	// Mirrors `comment` for synchronous reads from `flushPending`, which is
	// called imperatively and can't rely on the closure over render-time state.
	const commentRef = useRef(comment);
	const lastSavedRef = useRef(initialComment ?? "");
	const richTextEditorRef = useRef<RichTextEditorHandle>(null);

	useEffect(() => {
		commentRef.current = comment;
	}, [comment]);

	// Fetch comment from API only when caller didn't provide initialComment at all
	useEffect(() => {
		if (initialComment !== undefined) {
			return;
		}
		const fetchComment = async () => {
			try {
				const result = await getQuoteComment({ inviteId, quoteId });
				if (result?.comment) {
					setComment(result.comment);
				}
			} catch {
				// Error handled by global interceptor
			}
		};
		fetchComment();
	}, [initialComment, inviteId, quoteId]);

	const hasComment =
		comment &&
		comment.replace(/<[^>]*>/g, "").trim().length > 0;

	const handleSave = useCallback(
		async (value: string): Promise<boolean> => {
			// PRO-659: an empty `value` here can be a genuine clear, or it can be
			// the stale tail of Quill's onChange-time empty guard — RichTextEditor
			// already vouched for the value being genuinely empty when onChange
			// fired, but this save can run up to DEBOUNCE_MS later (the debounce
			// callback) or on an independent trigger entirely (flushPending, which
			// the tab-switch gate calls and which may fire well after the last
			// onChange). Re-read Quill's live text one more time, right before the
			// send, and skip the empty send if the editor now disagrees with the
			// value we're about to persist. This narrows the race window — it does
			// not turn the heuristic into a hard rule; it's still Quill vouching
			// for itself, just asked a second time closer to the actual POST.
			//
			// Returning `true` here (not `false`) is deliberate: `flushPending`'s
			// return value answers "is the persisted state correct?", not "did a
			// POST fire?" — BidderQuoteTab's tab-switch gate reduces all comment
			// flushes with `every(Boolean)`, so `false` reads as a save failure
			// and would block navigation or surface an error toast for what is
			// actually a successful defence: nothing needed saving, the
			// persisted comment is already right. Reserve `false` for a genuine
			// transport/API failure below.
			if (!value) {
				const liveText = richTextEditorRef.current?.getLiveText() ?? "";
				if (liveText.trim().length > 0) {
					return true;
				}
			}

			setIsSaving(true);
			try {
				await saveQuoteComment(
					{ inviteId, quoteId },
					{ comment: value }
				);
				lastSavedRef.current = value;
				onSaved?.(value);
				return true;
			} catch {
				// Error handled by global interceptor
				return false;
			} finally {
				setIsSaving(false);
			}
		},
		[inviteId, quoteId, onSaved]
	);

	const handleChange = useCallback(
		(value: string) => {
			setComment(value);

			if (isInitialMount.current) {
				isInitialMount.current = false;
				return;
			}

			if (debounceTimerRef.current) {
				clearTimeout(debounceTimerRef.current);
			}

			debounceTimerRef.current = setTimeout(() => {
				handleSave(value);
			}, DEBOUNCE_MS);
		},
		[handleSave]
	);

	// Cleanup debounce timer on unmount
	useEffect(() => {
		return () => {
			if (debounceTimerRef.current) {
				clearTimeout(debounceTimerRef.current);
			}
		};
	}, []);

	useImperativeHandle(ref, () => ({
		flushPending: async (): Promise<boolean> => {
			if (debounceTimerRef.current) {
				clearTimeout(debounceTimerRef.current);
				debounceTimerRef.current = null;
			}
			// Nothing edited since the last successful save — no-op, not a failure.
			if (commentRef.current === lastSavedRef.current) {
				return true;
			}
			return handleSave(commentRef.current);
		},
	}), [handleSave]);

	const sanitizedComment = useMemo(
		() => DOMPurify.sanitize(comment),
		[comment]
	);

	const defaultIndex = hasComment ? [0] : [];

	if (isReadOnly && !hasComment) {
		return null;
	}

	if (isReadOnly) {
		return (
			<div className="mt-4">
				<Text className="mb-2 font-roboto text-sm font-medium text-gray-700">
					Supplier Comment:
				</Text>
				<div
					className="prose prose-sm max-w-none rounded-md border border-gray-200 bg-gray-50 p-4 font-roboto"
					dangerouslySetInnerHTML={{ __html: sanitizedComment }}
					data-testid="quote-comment-readonly"
				/>
			</div>
		);
	}

	return (
		<div className="mt-4">
			<Accordion
				allowToggle
				defaultIndex={defaultIndex}
			>
				<AccordionItem className="!border-y-0">
					<AccordionButton className="w-fit rounded-md border border-gray-300 bg-white px-4 py-2 hover:!bg-gray-50">
						<FiMessageSquare className="mr-2 text-gray-500" />
						<Box
							as="span"
							flex="1"
							textAlign="left"
						>
							<Text className="font-roboto text-sm font-medium text-gray-700">
								{hasComment ? "Comment" : "Add Comment"}
							</Text>
						</Box>
						<AccordionIcon />
					</AccordionButton>
					<AccordionPanel className="!px-0 !pt-3 !pb-0">
						<RichTextEditor
							ref={richTextEditorRef}
							value={comment}
							onChange={handleChange}
							placeholder="Enter your comment here..."
							disabled={isReadOnly}
							guideLine="Add notes about your pricing, assumptions, or exclusions."
							testId="quote-comment-editor"
							noMargin
						/>
						{isSaving && (
							<Text
								className="mt-1 font-roboto text-xs text-blue-400"
								data-testid="quote-comment-saving"
								role="status"
							>
								Saving...
							</Text>
						)}
					</AccordionPanel>
				</AccordionItem>
			</Accordion>
		</div>
	);
});

QuoteComment.displayName = "QuoteComment";

export { QuoteComment };
